<template>
  <section class="cover-container" :class="{ 'in-view': sectionInView }">
    <!-- Background media: video if available, otherwise image via CSS -->
    <video v-if="videoSrc" autoplay muted loop class="cover-video">
      <source :src="videoSrc" type="video/mp4" />
    </video>

    <div class="cover-overlay"></div>

    <div class="cover-content">
      <div class="cover-left">
        <div class="eyebrow">VISION, MISSION & VALUES</div>
        <h1 class="cover-title">
          COMMUNITY,
          <br />INNOVATION,
          <br />SUCCESS
        </h1>
      </div>

      <!-- FAQ sits under the text, centered -->
      <div class="cover-right">
        <section class="faq-section">
		<div class="faq-inner">
			<div class="faq-header">
				<h2 class="faq-title">Frequently Asked Questions</h2>
				<p class="faq-subtitle">Answers that help you understand our programs and offerings.</p>
			</div>

			<div class="faq-list">
				<div
					v-for="(item, index) in faqs"
					:key="index"
					class="faq-item"
					:class="{ 'is-active': item.open }"
					@mouseenter="handleFaqEnter(index)"
					@mouseleave="handleFaqLeave"
				>
					<button
						class="faq-question"
						:aria-expanded="item.open.toString()"
						@click="handleFaqClick(index)"
					>
						<span class="question-badge">FAQ</span>
						<span class="question-text">{{ item.q }}</span>
						<span class="chev" :class="{ open: item.open }">▾</span>
					</button>

					<transition name="collapse">
						<div v-show="item.open" class="faq-answer">
							<p v-html="item.a"></p>
						</div>
					</transition>
				</div>
			</div>
		</div>
	</section>
     </div>
    </div>
  </section>
</template>

<script>

import api from '@/services/api';
import { resolveRichText } from '@/services/media';

export default {
  name: 'Cover',
  data() {
    return {
      videoSrc: null,
      isMobile: false,
      sectionInView: false,
      observer: null,
      faqs: [
        { q: 'What age groups does ALSHOLA School accept?', a: 'We accept students from early years through secondary levels.', open: false },
        { q: 'What curriculum do you follow?', a: 'Our curriculum blends international-minded approaches with local context.', open: false },
        { q: 'Do you offer online learning options?', a: 'Yes. We provide a flexible online learning platform.', open: false },
        { q: 'How can parents get involved?', a: 'Parents are encouraged to participate through events and conferences.', open: false }
      ]
    }
  },
  async created() {
    try {
      // Call individual endpoint: /api/faqs/
      const data = await api.getFAQs();
      const list = api.extractList(data);
      if (list.length > 0) {
        this.faqs = list.map(f => ({
          q: f.question,
          a: resolveRichText(f.answer || ''),
          open: false
        }));
      }
    } catch (e) {
      console.warn('[Cover/FAQ] API failed, using fallback:', e.message);
    }
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    try { this.videoSrc = require('../assets/video.mp4'); } catch (e) { this.videoSrc = null; }
    this.$nextTick(() => {
      const el = this.$el;
      if (!el) return;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) { this.sectionInView = true; if (this.observer) { this.observer.disconnect(); } } });
      }, { threshold: 0.3 });
      this.observer.observe(el);
    });
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
    if (this.observer) { this.observer.disconnect(); }
  },
  methods: {
    handleFaqClick(i) { if (!this.isMobile) return; this.toggle(i); },
    handleFaqEnter(i) { if (this.isMobile) return; this.faqs = this.faqs.map((f, idx) => ({ ...f, open: idx === i })); },
    handleFaqLeave() { if (this.isMobile) return; this.faqs = this.faqs.map((f) => ({ ...f, open: false })); },
    toggle(i) { this.faqs = this.faqs.map((f, idx) => ({ ...f, open: idx === i ? !f.open : false })); },
    checkMobile() { this.isMobile = window.innerWidth <= 1024; },
  },
}

</script>

<style scoped>
* { box-sizing: border-box; }
.cover-container {
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 600px;
  overflow: hidden;
  display: block;
  background-image: url('../assets/01.jpg'); /* fallback image; replace with your asset */
  background-size: cover;
  background-position: center center;
}

.cover-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
}

.cover-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.5) 60%, rgba(0,0,0,0.6) 100%);
  z-index: 2;
}

.cover-content {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column; /* text on top, FAQ under it */
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  padding: 40px 6%;
  gap: 30px;
}

.cover-left {
  width: 100%;
  color: white;
  text-shadow: 0 6px 20px rgba(0,0,0,0.6);
  text-align: center;
  flex-shrink: 0;
  max-width: 900px;
  margin: 0 auto 16px;
}

.eyebrow {
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 2px;
  color: rgba(255,255,255,0.95);
  margin-bottom: 8px;
}

.cover-title {
  font-size: 84px;
  line-height: 0.95;
  margin: 0;
  font-weight: 900;
  letter-spacing: -2px;
}

.cover-right {
  width: 100%;
  text-align: center;
  color: white;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  max-width: 1100px;
  margin: 0 auto;
}

.cover-caption {
  font-size: 14px;
  line-height: 1.7;
  margin-bottom: 24px;
  opacity: 0.95;
}

.cover-cta {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  background: linear-gradient(135deg,#C02130 0%,#F7F02B 100%);
  color: white;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(192,33,48,0.35);
}

.cover-cta .arrow { font-size: 18px; }

.faq-section {
	width: 100%;
	padding: 20px 1%;
}
.faq-inner {
	max-width: 1100px;
	margin: 0 auto;
}
.faq-header {
	text-align: center;
	margin-bottom: 10px;
}
.faq-title {
	font-size: 40px;
	font-weight: 800;
	color: #ffffff;
	letter-spacing: -1px;
}
.faq-subtitle {
	margin-top: 8px;
	color: #ffffff;
	font-size: 16px;
}
.faq-list {
	display: grid;
	gap: 18px;
	grid-template-columns: repeat(4, 1fr); /* four items in one row on wide screens */
	align-items: start;
	position: relative;
	padding: 20px 0;
	min-height: 400px;
}
.faq-item {
	background: #fff; /* make each FAQ white */
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 10px 30px rgba(0,0,0,0.08);
	border: 1px solid rgba(0,0,0,0.04);
	min-height: 160px;
	display: flex;
	flex-direction: column;

	position: relative;
  transition:
    opacity 0.5s ease,
    transform 0.5s ease,
    max-height 0.6s ease,
    box-shadow 0.4s ease;
}

/* Animate FAQ card when opening/closing */
.faq-item.is-active {
  transform: translateY(-4px);
  box-shadow: 0 18px 40px rgba(0,0,0,0.18);
}
.faq-item:not(.is-active) {
  transform: translateY(0);
}

/* Large screen behavior: one FAQ full-width, others hidden */
@media (min-width: 1025px) {
  /* When any FAQ is active: switch to single column and show only that item */
  .faq-list:has(.faq-item.is-active) {
    grid-template-columns: 1fr;
  }

  .faq-list:has(.faq-item.is-active) .faq-item {
    min-height: auto;
  }

  .faq-list:has(.faq-item.is-active) .faq-item:not(.is-active) {
    display: none;
  }

  .faq-item.is-active {
    box-shadow: 0 20px 60px rgba(0,0,0,0.25);
  }
}
.faq-question {
	width: 100%;
	padding: 20px 28px;
	display: block;
	position: relative;
	background: transparent;
	border: none;
	cursor: pointer;
	text-align: left;
}
.question-badge {
	display: block;
	background: linear-gradient(135deg,#C02130 0%,#F7F02B 100%);
	color: white;
	padding: 6px 10px;
	border-radius: 6px;
	font-weight: 800;
	font-size: 12px;
	margin-bottom: 10px;
	width: max-content;
}
.question-text {
	display: block;
	font-size: 18px;
	font-weight: 700;
	color: #2c3e50;
	padding-right: 34px; /* space for chevron */
}
.chev {
	transition: transform 0.35s cubic-bezier(.2,.9,.2,1);
	color: #2c3e50;
	font-size: 20px;
	position: absolute;
	top: 22px;
	right: 28px;
}
.chev.open { transform: rotate(180deg); }
.faq-answer {
	padding: 18px 28px 28px 28px;
	color: #555;
	line-height: 1.7;
	background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0));
}

/* Collapse transition - very smooth open/close */
.collapse-enter-active,
.collapse-leave-active {
  transition:
    max-height 0.75s cubic-bezier(0.25, 0.8, 0.25, 1),
    opacity 0.55s ease,
    padding-top 0.55s ease,
    padding-bottom 0.55s ease,
    transform 0.55s ease;
  overflow: hidden;
}
.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  transform: translateY(-6px);
}
.collapse-enter-to,
.collapse-leave-from {
  max-height: 600px;
  opacity: 1;
  transform: translateY(0);
}

/* Scroll-in animation for text + FAQ */
.cover-left,
.faq-section {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.7s ease, transform 0.7s ease;
}

.cover-container.in-view .cover-left {
  opacity: 1;
  transform: translateY(0);
}

.cover-container.in-view .faq-section {
  opacity: 1;
  transform: translateY(0);
  transition-delay: 0.15s;
}

/* Responsive */
@media (max-width: 1024px) {
  .cover-container { 
    height: auto;
  }
  .cover-content {
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }
	.cover-title { font-size: 56px; }
	.faq-section { padding: 10px 1%; }
	.faq-title { font-size: 36px; }
	.question-text { font-size: 16px; }
	.faq-list { grid-template-columns: repeat(3, 1fr); }
}

@media (min-width: 1025px) {
  .cover-left {
    text-align: center;
  }

  .cover-right {
    text-align: center;
  }
  .faq-list:has(.faq-item.is-active) {
    grid-template-columns: 1fr;
  }
  .faq-item.is-active {
    opacity: 1;
    transform: translateY(0) scale(1);
    max-height: 600px;
    z-index: 2;
  }


  .faq-list:has(.faq-item.is-active) .faq-item:not(.is-active) {
    opacity: 0;
    transform: translateY(20px) scale(0.96);
    max-height: 0;
    overflow: hidden;
    pointer-events: none;
  }
}

@media (max-width: 768px) {
	.cover-content { padding: 30px 20px; gap: 20px; }
	.cover-title { font-size: 42px; line-height: 1; }
	.eyebrow { font-size: 13px; }
	.faq-section { padding: 10px 1%; }
	.faq-inner { max-width: 100%; }
	.faq-title { font-size: 28px; }
	.faq-subtitle { font-size: 14px; }
	.question-text { font-size: 15px; }
	.faq-question { padding: 18px 16px; }
	.question-badge { font-size: 11px; padding: 5px 8px; }
	.chev { top: 18px; right: 16px; font-size: 18px; }
	.faq-answer { padding: 14px 16px 20px; font-size: 14px; }
	.faq-list { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 600px) {
	.cover-container { min-height: 500px; }
	.cover-content { padding: 24px 16px; gap: 16px; }
	.cover-title { font-size: 32px; margin-top: 16px; }
	.eyebrow { font-size: 11px; margin-bottom: 6px; }
	.faq-section { padding: 10px 0; }
	.faq-title { font-size: 24px; }
	.faq-subtitle { font-size: 13px; }
	.question-text { font-size: 14px; }
	.faq-item { min-height: 140px; }
	.faq-list { grid-template-columns: 1fr; gap: 14px; }
}

@media (max-width: 480px) {
	.cover-container { min-height: 450px; }
	.cover-content { padding: 20px 12px; gap: 12px; }
	.cover-title { font-size: 28px; }
	.eyebrow { font-size: 10px; }
	.faq-section { padding: 8px 0; }
	.faq-title { font-size: 20px; }
	.faq-subtitle { font-size: 12px; margin-top: 6px; }
	.question-text { font-size: 13px; }
	.faq-question { padding: 14px 12px; }
	.faq-answer { padding: 12px 12px 18px; font-size: 13px; line-height: 1.6; }
	.question-badge { font-size: 10px; padding: 4px 6px; margin-bottom: 8px; }
	.chev { top: 16px; right: 12px; font-size: 16px; }
	.faq-item { min-height: auto; }
}
</style>